import { LinkedAccountStatus, useCustomerDetails } from '@egw/api'
import { Alert, AlertType, Button, ButtonVariant, Field, ModalDrawer, PageHeader, useModalState } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { useRouter } from '@egw/router'
import { titleCapitalization } from '@egw/utils'
import { sendEvent } from '@egw/events'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { type FC, useState } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { PlaidLink, RemoveAccountForm, UpdateAccountForm } from '../../components'

export const ExternalAccountDetailsPage: FC = () => {
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const { isOpen: isRelinkModalOpen, openModal: openRelinkModal, closeModal: closeRelinkModal } = useModalState()

  const { t } = useTranslation('features-external-accounts')

  const { searchParams, navigate } = useRouter()
  const alias = Number(searchParams().get('alias'))

  const { data, isLoading, isError, isFetched, isFetching } = useCustomerDetails()
  const account = data?.customer.linkedAccounts.achAccounts.find((a) => a.alias === alias)

  return (
    <>
      {!isLoading && account ? (
        <PageHeader
          title={account.name}
          subtitle={t('details.external_accounts')}
          backLinkUrl={WEALTH_ROUTES.SETTINGS_BANK_TRANSFER_ACCOUNTS_DETAILS.backPath}
        />
      ) : null}
      {/* isFetched and !Account allows for removal of account, without showing a blank page while the Success Modal shows */}
      {isLoading || isFetching || (isFetched && !account) ? (
        <div className="egw-container">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={5} height="80px" />
          </SkeletonTheme>
        </div>
      ) : null}
      {account && !isLoading && !isFetching ? (
        <div className="egw-container pt-0">
          {isError ? (
            <div className="egw-container">
              <InformationCircleIcon className="w-12 h-12 fill-red-500 mb-4" />
              <p className="text-error text-2xl font-light mb-10">{t('details.error_title')}</p>
              <p className="text-body text-xl">{t('details.error_message')}</p>
            </div>
          ) : null}

          {/* Expired */}
          {account.linkedAccountStatus === LinkedAccountStatus.EXPIRED ? (
            <Alert
              alertType={AlertType.Error}
              description={t('details.expired_message')}
              className="mb-sm"
              ctaLabel={t('details.reconnect_cta')}
              ctaCallback={() => {
                openRelinkModal()
              }}
            />
          ) : null}

          {account ? (
            <div className="flex flex-col gap-2xs mb-lg">
              <Field label={t('details.bank_name')} value={account.institutionName} />
              <hr className="border-light" />
              <Field label={t('details.account_number')} value={account.mask} />
              <hr className="border-light" />
              <Field label={t('details.account_type')} value={titleCapitalization(account.achType)} />
              <hr className="border-light" />
              <Field
                label={t('details.nickname')}
                value={account.name}
                onEdit={() => {
                  setIsUpdateModalOpen(true)
                }}
              />
            </div>
          ) : null}

          <div className="flex gap-2xs items-center">
            {account.linkedAccountStatus === LinkedAccountStatus.EXPIRING_SOON ? (
              <Button variant={ButtonVariant.Primary} onClick={openRelinkModal}>
                {t('details.relink_account_title')}
              </Button>
            ) : null}

            <Button
              variant={ButtonVariant.Secondary}
              onClick={() => {
                setIsDeleteModalOpen(true)
              }}
              testId="removeAccount_secondary_btn"
              isDisabled={data?.customer.linkedAccounts.isSingleRestricted}
            >
              {t('details.remove_account')}
            </Button>
          </div>
        </div>
      ) : null}

      {account ? (
        <>
          <ModalDrawer
            isOpen={isUpdateModalOpen}
            onClose={() => {
              setIsUpdateModalOpen(false)
            }}
            title={t('details.edit_nickname')}
          >
            <UpdateAccountForm
              alias={alias}
              onSuccess={() => {
                setIsUpdateModalOpen(false)
              }}
            />
          </ModalDrawer>

          <ModalDrawer
            isOpen={isDeleteModalOpen}
            onClose={() => {
              setIsDeleteModalOpen(false)
            }}
            title={t('details.remove_title')}
          >
            <RemoveAccountForm
              alias={account.alias}
              onSuccess={() => {
                // Sets Account Name that was just removed
                navigate(WEALTH_ROUTES.SETTINGS_BANK_TRANSFER_ACCOUNTS.path)
              }}
              onCloseModal={() => {
                setIsDeleteModalOpen(false)
              }}
            />
          </ModalDrawer>
          <ModalDrawer title={t('details.relink_account')} onClose={closeRelinkModal} isOpen={isRelinkModalOpen}>
            <PlaidLink
              isRelinking
              onPlaidExit={closeRelinkModal}
              onClose={() => {
                sendEvent('eg_linkInstantly_fail', {
                  eventSource: 'egWealth',
                  category: 'instant-link',
                  subCategory: 'result',
                  failureReason: 'cancelled',
                })
                closeRelinkModal()
              }}
            />
          </ModalDrawer>
        </>
      ) : null}
    </>
  )
}
